import type { AIProviderId } from "./aiProviders";
import { getAIProvider } from "./aiProviders";
import { isSqliteMediaType, mediaTypeFromFileName } from "./fileMediaTypes";

export { isSqliteMediaType, mediaTypeFromFileName } from "./fileMediaTypes";

// Browsers derive `file.type` from the extension, so only magic bytes can
// catch a mislabeled image (e.g. an AVIF named .png) that the AI SDK would
// re-detect from content and the model provider then reject.
const IMAGE_SIGNATURES = new Map<string, Array<number | null>>([
  ["image/jpeg", [0xff, 0xd8]],
  ["image/png", [0x89, 0x50, 0x4e, 0x47]],
  ["image/gif", [0x47, 0x49, 0x46]],
  [
    "image/webp",
    [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50],
  ],
]);

export const IMAGE_TYPES = new Set(IMAGE_SIGNATURES.keys());

const TEXT_LIKE_TYPES = new Set([
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "text/xml",
  "application/json",
  "application/jsonl",
  "application/toml",
  "application/xml",
  "application/yaml",
]);

const ARCHIVE_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-tar",
  "application/gzip",
  "application/x-gzip",
]);

export type PreviewKind =
  | "image"
  | "text"
  | "csv"
  | "sqlite"
  | "audio"
  | "video"
  | "fallback";

export function isCsvMediaType(mediaType: string): boolean {
  return mediaType === "text/csv";
}

export function isArchiveMediaType(mediaType: string): boolean {
  return ARCHIVE_TYPES.has(mediaType);
}

export function previewKind(mediaType: string): PreviewKind {
  if (IMAGE_TYPES.has(mediaType)) return "image";
  if (isSqliteMediaType(mediaType)) return "sqlite";
  if (isCsvMediaType(mediaType)) return "csv";
  if (mediaType.startsWith("audio/")) return "audio";
  if (mediaType.startsWith("video/")) return "video";
  if (TEXT_LIKE_TYPES.has(mediaType) || mediaType.startsWith("text/")) {
    return "text";
  }
  return "fallback";
}

export type UploadRejection = { status: 413 | 415; message: string };

export function sniffImageMediaType(bytes: Uint8Array): string | null {
  for (const [mediaType, signature] of IMAGE_SIGNATURES) {
    if (signature.every((byte, i) => byte === null || bytes[i] === byte)) {
      return mediaType;
    }
  }
  return null;
}

function imageBytesMatch(head: Uint8Array, mediaType: string): boolean {
  const signature = IMAGE_SIGNATURES.get(mediaType);
  return (
    !signature ||
    signature.every((byte, i) => byte === null || head[i] === byte)
  );
}

export function rejectUpload(
  bytes: Uint8Array,
  mediaType: string,
  limit: number,
  subject: string
): UploadRejection | null {
  if (bytes.byteLength > limit) {
    return { status: 413, message: `${subject} is too large` };
  }
  if (!imageBytesMatch(bytes, mediaType)) {
    const label = mediaType.split("/")[1]!.toUpperCase();
    return {
      status: 415,
      message: `${subject} isn’t a valid ${label} image`,
    };
  }
  return null;
}

// Stored bytes are uploaded out-of-band, so callers re-check them against a declared type rather than trusting a mutation’s claim.
export function attachmentBytesValid(
  base64: string,
  mediaType: string
): boolean {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  const sizeBytes = Math.floor((base64.length * 3) / 4) - padding;
  if (sizeBytes > attachmentSizeLimit(mediaType)) return false;
  return imageBytesMatch(Buffer.from(base64.slice(0, 16), "base64"), mediaType);
}

export const MAX_IMAGE_SIZE = Math.floor((5 * 1024 * 1024 * 3) / 4);
export const MAX_PDF_SIZE = 32 * 1024 * 1024;
export const MAX_DOCUMENT_SIZE = Math.floor(4.5 * 1024 * 1024);
// Blobstore caps a single volume file write at 32 MiB.
export const MAX_VOLUME_FILE_SIZE = 32 * 1024 * 1024;

function getAttachmentSupport(providerId: AIProviderId) {
  return getAIProvider(providerId).attachmentSupport;
}

export function attachmentHandling(
  providerId: AIProviderId,
  mediaType: string
): "image" | "file" | "inline-text" | null {
  if (IMAGE_TYPES.has(mediaType)) return "image";
  const support = getAttachmentSupport(providerId);
  if (support.inlineTextMediaTypes?.includes(mediaType)) return "inline-text";
  return support.documentMediaTypes.includes(mediaType) ? "file" : null;
}

export function attachmentMediaType(file: Pick<File, "name" | "type">) {
  const fromName = mediaTypeFromFileName(file.name);
  if (IMAGE_TYPES.has(file.type)) return file.type;
  return fromName === "application/octet-stream"
    ? file.type || fromName
    : fromName;
}

export function resolveAttachmentMediaType(
  file: Pick<File, "name" | "type">,
  providerId: AIProviderId
) {
  const mediaType = attachmentMediaType(file);
  return attachmentHandling(providerId, mediaType) ? mediaType : null;
}

export function maxImageDimensionFor(providerId: AIProviderId) {
  return getAttachmentSupport(providerId).maxImageDimension;
}

// Volume uploads go to the model only when read, as images through
// readStorageFile, so only images keep the model's cap.
export function volumeAttachmentSizeLimit(mediaType: string) {
  return IMAGE_TYPES.has(mediaType) ? MAX_IMAGE_SIZE : MAX_VOLUME_FILE_SIZE;
}

export function volumeAttachmentSizeError(attachment: {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
}): string | null {
  const limit = volumeAttachmentSizeLimit(attachment.mediaType);
  return attachment.sizeBytes > limit
    ? `${attachment.fileName} is larger than ${limit / (1024 * 1024)} MB`
    : null;
}

export function attachmentSizeLimit(mediaType: string) {
  if (IMAGE_TYPES.has(mediaType)) return MAX_IMAGE_SIZE;
  if (mediaType === "application/pdf") return MAX_PDF_SIZE;
  return MAX_DOCUMENT_SIZE;
}
